import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { useEffectEvent } from 'use-effect-event';
import IntegrationCardContent from './IntegrationCardContent';
import * as styles from './IntegrationDetailPreview.module.scss';
import IntegrationMentionError from './IntegrationMentionError';
import EDITOR_INTEGRATION_MENTIONS from '../editor/mentions/EditorIntegrationMentions';
import QuillContext from '../editor/QuillContext';
import getEditorMentionUrl from '../editor/utils/getEditorMentionUrl';
import { gql } from '../graphql/generated';
import PostPreviewQuillComponent from '../post/PostPreviewQuillComponent';
import NestableLink from '../utils/NestableLink';

const MENTION_QUERY = gql(/** @complexity 68 */ `
  query IntegrationDetailPreview($type: MentionType!, $id: ID!) {
    mention(type: $type, id: $id) {
      preview {
        content
      }
      # eslint-disable-next-line @graphql-eslint/require-selections
      ...IntegrationCardContent_mention
    }
  }
`);

interface IntegrationDetailPreviewProps {
  id: string;
  type: string;
  params?: string;
  onPreviewLoadChange?: (isLoaded: boolean) => void;
}

const IntegrationDetailPreview = ({
  id,
  type,
  params,
  onPreviewLoadChange,
}: IntegrationDetailPreviewProps) => {
  const apiEnum = EDITOR_INTEGRATION_MENTIONS[type]?.apiEnum;

  const {
    data: { mention } = {},
    error,
    refetch,
    loading,
  } = useQuery(MENTION_QUERY, {
    skip: !apiEnum,
    variables: { id, type: apiEnum! },
  });

  const to = getEditorMentionUrl({ type, id, params });

  const onPreviewLoadChangeEvent = useEffectEvent(
    onPreviewLoadChange ?? (() => {}),
  );

  useEffect(() => {
    onPreviewLoadChangeEvent(!loading);
  }, [loading]);

  if (mention?.preview?.content) {
    return (
      <NestableLink
        className={styles.previewContentContainer}
        ignoreInsideLinks
        to={to}
      >
        <QuillContext>
          <PostPreviewQuillComponent content={mention.preview.content} />
        </QuillContext>
      </NestableLink>
    );
  }

  if (error) {
    return (
      <IntegrationMentionError type={type} error={error} refetch={refetch} />
    );
  }

  if (mention) {
    return (
      <NestableLink to={to} newTab className={styles.container}>
        <IntegrationCardContent mention={mention} />
      </NestableLink>
    );
  }

  return null;
};

export default IntegrationDetailPreview;
